import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, ProgressBar, OverlayTrigger, Tooltip } from "react-bootstrap";
import axios from "axios";
import { CancelIcon, CloseIcon, EditIcon, PLusIcon, SaveIcon, UploadIcon } from "../assets/Icons";
import Select, { components } from "react-select";
import { fileTypeIcons } from "../constant/constant";
import { UNO_URL } from "../Server";


const FileUploaderModal = ({ show, onClose, onUploadComplete, fileType, token, folderId, getDriveFolderFiles, tagsList, getStorageCategoryTags }) => {
  const [files, setFiles] = useState([]);
  const [storageCategoryId, setStorageCategoryId] = useState(null);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [editingFileId, setEditingFileId] = useState(null);
  const [tempName, setTempName] = useState("");
  const [loading, setLoading] = useState(false);
  const dropRef = useRef();

  const handleCreateNewTag = async () => {
    if (!inputValue.trim()) return;
    setLoading(true);
    try {
      let bodyData = {
        name: inputValue.trim(),
        categoryId: Number(storageCategoryId)
      }

      await axios.post(`${UNO_URL}/api/tag/create`, bodyData, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      getStorageCategoryTags();

    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    const storageConfig = localStorage.getItem("configuration");
    const configData = JSON.parse(storageConfig);
    const _storageCategoryId = configData?.storageCategoryId || null;
    setStorageCategoryId(_storageCategoryId);
    let _tagsList = (tagsList || []).map(e => ({ id: e.id, value: e.name, label: e.name }));
    setTagsOptions(_tagsList);

    if (!show) {
      setFiles([]);
      setSelectedTags([]);
      setEditingFileId(null);
      setTempName("");
    }
  }, [show, tagsList]);

  const handleCloseModal = () => {
    setFiles([]);
    setEditingFileId(null);
    setTempName("");
    onClose();
  }

  const addFiles = (selectedFiles) => {
    const newFiles = selectedFiles.map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}`,
      progress: 0,
      status: "pending",
      cancelTokenSource: null,
      response: null,
      displayName: (file.name).split(".").slice(0, -1).join("."),
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    uploadFiles(newFiles);
  };

  const uploadFiles = async (fileItems) => {
    for (const item of fileItems) {
      if (item.status !== "pending") continue;
      const cancelTokenSource = axios.CancelToken.source();
      setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "uploading", cancelTokenSource } : f)));
      const form = new FormData();
      form.append("file", item.file);
      try {
        const resp = await axios.post(`${UNO_URL}/api/drive/upload/${folderId}`, form, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          cancelToken: cancelTokenSource.token,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total) || 0;
            setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, progress: percent < 99 ? percent : 99 } : f)));
          },
        });

        setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "done", progress: 100, response: resp.data } : f)));

      } catch (err) {
        if (axios.isCancel(err)) {
          setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "canceled" } : f)));
        } else {
          setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "error" } : f)));
        }
      }
    }

    if (typeof onUploadComplete === "function") {
      const uploaded = files
        .filter((f) => f.status === "done")
        .map((f) => ({
          name: f.displayName,
          size: f.file.size,
          response: f.response,
        }));
      onUploadComplete(uploaded);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) { addFiles([file]) }
    // const selected = Array.from(e.target.files);
    // if (selected.length) addFiles(selected);
    e.target.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add("dragover");
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("dragover");
    const file = e.dataTransfer.files[0]
    if (file) { addFiles([file]) }

    // const dropped = Array.from(e.dataTransfer.files);
    // if (dropped.length) addFiles(dropped);
  };

  const handleCancelUpload = (id) => {
    const item = files.find((f) => f.id === id);
    if (!item) return;
    if (item.cancelTokenSource) {
      item.cancelTokenSource.cancel("User canceled upload");
    } else {
      setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: "canceled" } : f)));
    }
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Start editing
  const startEditing = (file) => {
    setEditingFileId(file.id);
    setTempName(file.displayName);
  };

  // Save new name
  const saveName = (id) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, displayName: tempName } : f)));
    setEditingFileId(null);
    setTempName("");
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingFileId(null);
    setTempName("");
  };

  const selectAllId = "uploader-file-input";

  const handleSaveFile = async () => {
    let allFileUploaded = (files.length > 0 && files.every(e => e.progress == 100));
    if (!allFileUploaded) return;

    try {
      let fileToSave = files.map(e => ({
        "folderId": folderId,
        "mimeType": e.file.type,
        "name": e.displayName,
        "url": e.response.url
      }));

      let bodyData = {
        files: fileToSave,
        tags: selectedTags.map(e => e.id)
      }

      await axios.post(`${UNO_URL}/api/drive/save/multiple/file`, bodyData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      handleCloseModal();
      getDriveFolderFiles();

    } catch (error) {
      console.log(error);
    }
  }

  const NoOptionsMessage = (props) => (
    <components.NoOptionsMessage {...props}>

      {loading ?
        <>
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p>Creating tag</p>
        </>
        :
        <div
          style={{
            padding: "8px",
            cursor: "pointer",
            color: "#2f3a79",
            textDecoration: "underline",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            handleCreateNewTag();
          }}
        >
          <PLusIcon /> Add Tag
        </div>
      }
    </components.NoOptionsMessage>
  );

  return (
    <Modal show={show} onHide={handleCloseModal} centered size="lg">
      <Modal.Header>
        <Modal.Title>Upload Files</Modal.Title>
        <button onClick={handleCloseModal} className="close-s schedule-modal-button" type="button" title="Close">
          <CloseIcon />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div
          ref={dropRef}
          className="file-drop-zone text-center p-4 border"
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{ borderRadius: ".5rem" }}
        >
          <input
            id={selectAllId}
            type="file"
            accept={fileTypeIcons[fileType].accept || "*/*"}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <label htmlFor={selectAllId} style={{ cursor: "pointer" }}>
            <div style={{ fontSize: 16, marginBottom: 6 }}>
              <UploadIcon /> <br /> Drag & drop files here
            </div>
            <div style={{ fontSize: 13, color: "#666" }}>
              or <u>browse</u> to select {fileType} files
            </div>
          </label>
        </div>

        <div className="mt-3 uploader-file-list poition-relative">
          {files.length === 0 && <p className="text-muted text-center">No files selected</p>}
          {files.map((f) => (
            <div key={f.id} className="uploader-file-card d-flex align-items-center p-2 mb-2">
              <div className="flex-grow-1 poition-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div style={{ flex: 0.7 }}>
                    {editingFileId === f.id ? (
                      <div className="editfile d-flex align-items-center">
                        <input
                          type="text"
                          value={tempName}
                          onChange={(e) => setTempName(e.target.value)}
                          style={{ marginRight: 8 }}
                          className="form-control"
                        />
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-share">Save Edited</Tooltip>}>
                          <span className="save cursor-pointer" onClick={() => saveName(f.id)}>
                            Save
                          </span>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-share">Cancel</Tooltip>}>
                          <span onClick={cancelEditing} className="ms-2 cancel cursor-pointer">
                            Cancel
                          </span>
                        </OverlayTrigger>
                      </div>
                    ) : (
                      <div className="file-name m-0" style={{ minHeight: "inherit" }}>
                        <span>{f.displayName}</span>{" "}
                        <span className="fileedit" onClick={() => startEditing(f)} style={{ cursor: "pointer" }}>
                          <EditIcon />
                        </span>
                      </div>
                    )}
                    <div className="file-meta text-muted m-0" style={{ fontSize: 12 }}>
                      {Math.round(f.file.size / 1024)} KB
                    </div>
                  </div>

                  <div style={{ minWidth: 140 }}>
                    <div className="d-flex justify-content-end pb-2">
                      {(f.status === "done" || f.status === "error" || f.status === "canceled") && (
                        <>
                          <span className="removefile" onClick={() => removeFile(f.id)}>
                            <CancelIcon />
                          </span>
                        </>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <div style={{ flex: 1 }}>
                        <ProgressBar now={f.progress} label={`${f.progress}%`} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="addtags">
            <Select
              isMulti
              name="Tags"
              className="basic-multi-select mt-2"
              classNamePrefix="select"
              placeholder="Select Tags"
              options={tagsOptions}
              value={selectedTags}
              onChange={(selected) => setSelectedTags(selected || [])}
              components={{ NoOptionsMessage }}
              inputValue={inputValue} // controlled input
              onInputChange={(value) => setInputValue(value)}
              filterOption={(option, search) => {
                if (!option || !option.label) return false; // avoid crash
                return option.label.toLowerCase().includes(search.toLowerCase());
              }}
            />
            {/* <input type="text" className="form-control" placeholder="Add Tag"/> */}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="py-3 justify-content-end">
        <Button variant="primary" onClick={handleSaveFile}> Save </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FileUploaderModal;
